import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/** A stored attachment as returned by the API (metadata only — never bytes). */
export interface FormAttachment {
  id: string
  submissionId: string
  fieldId: string
  /** Position on the field. Always 0 except on a multi-file Attachment field. */
  slot?: number
  fileName: string
  mimeType: string
  sizeBytes: number
  storageKey: string
  uploadedBy: string
  uploadedAt: string
}

/** Everything the browser needs to PUT the file straight to storage. */
interface PresignedUpload {
  url: string
  method: 'PUT'
  headers: Record<string, string>
  storageKey: string
  expiresInSeconds: number
  fileName: string
  mimeType: string
  sizeBytes: number
}

export interface AttachmentDownload {
  url: string
  fileName: string
  mimeType: string
  sizeBytes: number
}

/**
 * The preview endpoint's answer.
 *
 * A discriminated union rather than a nullable url, because "this file cannot
 * be rendered" is a NORMAL answer, not a failure — the server decides before it
 * signs anything, so the panel shows its "use Download" message on
 * `previewable: false` and never on an error path.
 */
export type AttachmentPreview =
  | { previewable: true; url: string; fileName: string; mimeType: string; sizeBytes: number }
  | { previewable: false; fileName: string; mimeType: string; sizeBytes: number }

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // `headers` must be spread LAST — `init` (e.g. a caller's explicit
  // Content-Type) is applied first, then authHeaders() layers Authorization +
  // X-Tenant-Id on top. The reverse order lets `...init` clobber the whole
  // `headers` object whenever a caller passes one, silently dropping the
  // bearer token and producing a 401 on every such call.
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) {
        message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
      }
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

/**
 * The `attachmentId` query that names one file on a multi-file Attachment field.
 * A single-file field holds one row, so the server needs no id for it — and
 * refuses to guess when a field holds several.
 */
function which(attachmentId: string | undefined): string {
  return attachmentId ? `?attachmentId=${encodeURIComponent(attachmentId)}` : ''
}

export const attachmentsApi = {
  list: (submissionId: string) =>
    request<FormAttachment[]>(`/form-submissions/${submissionId}/attachments`),

  /**
   * Attachments for several submissions in ONE request (z8nrz7cnhz).
   *
   * The case page needs the evidence filed across a whole procedure. Asking per
   * submission meant a burst of N requests on every case open, which against the
   * API's 120/min ceiling is how a clinician came to see a rate-limit error in
   * place of a patient.
   *
   * Returns a map keyed by submission id. Every id the caller is allowed to read
   * gets a key — an empty array when it has no files — so a missing key means
   * "not readable in this tenant", not "no attachments".
   */
  listForSubmissions: (submissionIds: string[]) =>
    request<Record<string, FormAttachment[]>>(
      `/form-submissions/attachments/batch?submissionIds=${encodeURIComponent(
        submissionIds.join(','),
      )}`,
    ),

  /**
   * Three-step upload, matching the backend contract: presign → PUT the bytes
   * directly to storage → confirm.
   *
   * The middle step deliberately uses a bare `fetch` with NO auth headers: the
   * presigned URL carries its own signature, and sending an Authorization header
   * to S3 alongside a presigned query signature makes it reject the request.
   * `presigned.headers` must be forwarded verbatim or the signature won't match.
   */
  async upload(submissionId: string, fieldId: string, file: File): Promise<FormAttachment> {
    const presigned = await request<PresignedUpload>(
      `/form-submissions/${submissionId}/attachments/${fieldId}/presign`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
        }),
      },
    )

    const put = await fetch(presigned.url, {
      method: presigned.method,
      headers: presigned.headers,
      body: file,
    })
    if (!put.ok) {
      throw new Error(`Upload failed (${put.status}). The file was not saved.`)
    }

    // Only this step creates the row, so an abandoned upload leaves no orphan.
    return request<FormAttachment>(
      `/form-submissions/${submissionId}/attachments/${fieldId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storageKey: presigned.storageKey,
          fileName: presigned.fileName,
          mimeType: presigned.mimeType,
          sizeBytes: presigned.sizeBytes,
        }),
      },
    )
  },

  getDownloadUrl: (submissionId: string, fieldId: string, attachmentId?: string) =>
    request<AttachmentDownload>(
      `/form-submissions/${submissionId}/attachments/${fieldId}/download${which(attachmentId)}`,
    ),

  /**
   * An INLINE url for viewing the file in the app.
   *
   * Not the download url with a different name: that one is signed with
   * `Content-Disposition: attachment`, so pointing it at a viewer saves the file
   * instead of rendering it. Same permission behind both (`submissions.read`),
   * so a user who can download can preview and nobody else can.
   *
   * Short-lived and single-use, like the download url — fetch it when a preview
   * is actually opened, never hold it in a query cache.
   */
  getPreview: (submissionId: string, fieldId: string, attachmentId?: string) =>
    request<AttachmentPreview>(
      `/form-submissions/${submissionId}/attachments/${fieldId}/preview${which(attachmentId)}`,
    ),

  remove: (submissionId: string, fieldId: string, attachmentId?: string) =>
    request<{ ok: true }>(
      `/form-submissions/${submissionId}/attachments/${fieldId}${which(attachmentId)}`,
      { method: 'DELETE' },
    ),
}
